import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Star, ArrowUpDown, Map as MapIcon } from 'lucide-react'
import Header from '../components/Header'
import TabBar from '../components/TabBar'
import ScrollToTopButton from '../components/ScrollToTopButton'
import LocationBanner from '../components/LocationBanner'
import SortModal, { type SortMode } from '../components/SortModal'
import StoreRecoBadge from '../components/StoreRecoBadge'
import { useStore, filterVisibleStores, type Store } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import { useGeolocation } from '../hooks/useGeolocation'
import { useStoreReco } from '../hooks/useStoreReco'
import { haversineDistance } from '../utils/distance'
import { GENRES, GENRE_KEY, genreOf, storeTypeLabelKey, storeVisualKey, type Genre } from '../lib/genre'

// #1082 一覧フィルタはジャンル（体験/飲食/お土産/その他）で分ける。
// #329 note: キッチンカー専用フィルタは付けない — 客は「キッチンカーを探す」の
// ではなく通常の一覧・地図の中でたまたま出会う。出店状態はカードのバッジで示す。
type Filter = 'All' | Genre
const FILTERS: Filter[] = ['All', ...GENRES]

const categoryIcons: Record<string, string> = {
  restaurant: '🍽️',
  cafe: '☕',
  izakaya: '🏮',
  bar: '🍸',
  ramen: '🍜',
  sushi: '🍣',
  bakery: '🥐',
  sweets: '🍰',
  kitchencar: '🚐',
  shop: '🛍️',
  hotel: '🏨',
  other: '🏪',
  // #1661 業態キー（飲食以外は category を持たないので genre で引く）
  food: '🍽️',
  souvenir: '🛍️',
  experience: '🎋',
}

/** 一覧の「現金のみ」バッジを出すか。メニューを見せるだけの店 (#2115) はアプリで払わないので出さない */
const showCashOnly = (store: Store): boolean => !!store.cashOnlyMode && store.orderingMode !== 'menu-only'

export default function NearbyStores() {
  const navigate = useNavigate()
  const { stores: localStores, setCurrentStore } = useStore()
  const { t } = useI18n()
  const { status, coords, cityInfo, requestLocation } = useGeolocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const catParam = searchParams.get('cat') ?? ''
  const selectedFilter: Filter = (FILTERS as string[]).includes(catParam) ? (catParam as Filter) : 'All'
  const searchQuery = searchParams.get('q') ?? ''
  const setSelectedFilter = (f: Filter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (f === 'All') next.delete('cat')
      else next.set('cat', f)
      return next
    }, { replace: true })
  }
  const setSearchQuery = (q: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (!q) next.delete('q')
      else next.set('q', q)
      return next
    }, { replace: true })
  }
  const [apiStores, setApiStores] = useState<Store[]>([])
  // #8: null = 未選択 (自動)。おすすめスコアが取れていればおすすめ順、
  // 取れていなければ従来の評価順を既定にする。
  const [sortMode, setSortMode] = useState<SortMode | null>(null)
  const [sortModalOpen, setSortModalOpen] = useState(false)
  const { scores: recoScores, recoAvailable } = useStoreReco()
  const effectiveSort: SortMode = sortMode ?? (recoAvailable ? 'recommended' : 'rating')
  const [showAllAreas, setShowAllAreas] = useState(false)

  useEffect(() => {
    api.getStores()
      .then((data) => { if (data.stores) setApiStores(data.stores) })
      .catch(() => {})
  }, [])

  // 初回マウント時に位置情報を要求（idle のときのみ）
  useEffect(() => {
    if (status === 'idle') requestLocation()
  }, [status, requestLocation])

  const allStores = apiStores.length > 0 ? apiStores : localStores
  const stores = filterVisibleStores(allStores)

  // 距離を Haversine で付与（coords あるときのみ）
  const storesWithDistance = useMemo(() => {
    if (!coords) return stores.map((s) => ({ ...s, computedDistance: null as number | null }))
    return stores.map((s) => ({
      ...s,
      computedDistance: haversineDistance(coords.lat, coords.lng, s.latitude, s.longitude),
    }))
  }, [stores, coords])

  const normalizedQuery = searchQuery.trim().toLowerCase()

  // 検索のみ適用（カテゴリ未適用）— area scope 決定をカテゴリ非依存にするため
  const searchFiltered = storesWithDistance.filter((s) => {
    if (!normalizedQuery) return true
    return (
      s.name.toLowerCase().includes(normalizedQuery) ||
      s.nameEn.toLowerCase().includes(normalizedQuery) ||
      s.address.toLowerCase().includes(normalizedQuery)
    )
  })

  // ジャンル + 検索で絞り込み
  const baseFiltered = searchFiltered.filter(
    (s) => selectedFilter === 'All' || genreOf(s) === selectedFilter,
  )

  // 市区町村一致フィルタ（カテゴリ非依存で area scope を決定）:
  // 全タブで同じ area scope を共有することで、All タブが他タブの上位集合になる UX を保証する。
  // (Issue #487: 以前は category-filtered ベースで判定していたため、タブ毎に scope が分かれて
  //  All が他タブより少ない件数になっていた)
  // 1. cityInfo あり + showAllAreas=false →
  //    a) 市内（カテゴリ非依存）の店舗数が CITY_MIN_THRESHOLD 以上なら市内のみ
  //    b) 市内が CITY_MIN_THRESHOLD 未満で県内に店舗があれば県内に拡張
  //    c) 県内に 0 件なら全件
  const CITY_MIN_THRESHOLD = 3
  let cityFiltered: typeof baseFiltered = baseFiltered
  let areaExpanded = false
  if (cityInfo && !showAllAreas) {
    const cityScopeCount = searchFiltered.filter(
      (s) => s.prefecture === cityInfo.prefecture && s.city === cityInfo.city,
    ).length
    const prefScopeCount = searchFiltered.filter(
      (s) => s.prefecture === cityInfo.prefecture,
    ).length

    if (cityScopeCount >= CITY_MIN_THRESHOLD) {
      cityFiltered = baseFiltered.filter(
        (s) => s.prefecture === cityInfo.prefecture && s.city === cityInfo.city,
      )
    } else if (prefScopeCount > 0) {
      cityFiltered = baseFiltered.filter((s) => s.prefecture === cityInfo.prefecture)
      areaExpanded = true
    } else {
      cityFiltered = baseFiltered
      areaExpanded = true
    }
  }

  // ソート
  const displayed = [...cityFiltered].sort((a, b) => {
    if (effectiveSort === 'distance') {
      const da = a.computedDistance ?? Number.MAX_SAFE_INTEGER
      const db = b.computedDistance ?? Number.MAX_SAFE_INTEGER
      return da - db
    }
    // #8 おすすめ順: 実利用データ由来スコアの降順。スコアが無い店同士は
    // 従来どおり評価順に落ちる (集計対象外の店を隠さない)。
    if (effectiveSort === 'recommended') {
      const sa = recoScores[a.id]?.score ?? 0
      const sb = recoScores[b.id]?.score ?? 0
      if (sa !== sb) return sb - sa
    }
    return b.rating - a.rating
  })

  const handleStoreClick = (store: (typeof stores)[number]) => {
    setCurrentStore(store)
    navigate(`/stores/${store.id}`)
  }

  const distanceAvailable = coords !== null
  const showLocationBanner =
    status === 'denied' || status === 'unavailable' || cityInfo !== null

  return (
    <div className="min-h-screen bg-warm-white">
      {/* Header */}
      <Header
        title={t('stores.title')}
        onBack={() => navigate(-1)}
        rightAction={
          <button
            type="button"
            onClick={() => navigate(`/stores/map?${searchParams.toString()}`)}
            aria-label={t('stores.mapView')}
            className="w-10 h-10 flex items-center justify-center rounded-full tap-effect hover:bg-gray-50 transition-colors"
          >
            <MapIcon size={22} className="text-text-primary" />
          </button>
        }
      />

      {/* Search Bar */}
      <div className="px-4 mt-2">
        <div className="relative">
          <Search
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('stores.search')}
            className="w-full h-12 bg-gray-100 rounded-xl pl-12 pr-4 text-sm font-body text-text-primary placeholder:text-text-disabled outline-none focus:ring-2 focus:ring-yokoso-red/20"
          />
        </div>
      </div>

      {/* Location Banner */}
      {showLocationBanner && (
        <LocationBanner
          status={status}
          cityInfo={cityInfo}
          matchCount={cityFiltered.length}
          cityFiltered={cityInfo !== null && !showAllAreas && !areaExpanded}
          areaExpanded={areaExpanded}
          onRequestLocation={requestLocation}
          onShowAllAreas={() => setShowAllAreas(true)}
        />
      )}

      {/* Filter Chips + Sort */}
      <div className="mt-4 px-4 flex items-center gap-2">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-1">
          {FILTERS.map((filter) => {
            const isSelected = selectedFilter === filter
            return (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`rounded-full px-4 py-2 text-sm font-body font-medium whitespace-nowrap shrink-0 transition-colors tap-effect ${
                  isSelected
                    ? 'bg-yokoso-red text-white'
                    : 'bg-white border border-gray-200 text-text-primary'
                }`}
              >
                {filter === 'All' ? t('stores.all') : t(`stores.genre${GENRE_KEY[filter]}`)}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={() => setSortModalOpen(true)}
          aria-label={t('stores.sortLabel')}
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-white border border-gray-200 tap-effect"
        >
          <ArrowUpDown size={18} className="text-text-primary" />
        </button>
      </div>

      {/* Store Count */}
      <div className="px-4 mt-4">
        <p className="text-text-secondary text-sm font-body">
          {/* 旧 stores.within「◯件が500m以内」は距離フィルタが存在しないのに
            * 500m を謳う嘘ラベルだったため、件数だけの表現に変更 */}
          {t('stores.storeCount', { count: displayed.length })}
        </p>
      </div>

      {/* Store List */}
      <div className="flex flex-col gap-3 px-4 mt-3 pb-[83px]">
        {displayed.map((store) => {
          // 位置情報が取れていないときは距離を出さない。以前は seed のダミー距離
          // (store.distance) を実測のように「◯m」表示していた（モックデモ時代の名残で、
          // 根拠のない数字を本物に見せていた — #8 レビュー時の指摘）
          const distanceLabel =
            store.computedDistance != null
              ? store.computedDistance < 1000
                ? `${Math.round(store.computedDistance)}m`
                : `${(store.computedDistance / 1000).toFixed(1)}km`
              : null
          return (
            <button
              key={store.id}
              onClick={() => handleStoreClick(store)}
              className="flex flex-row bg-white rounded-xl shadow-card p-3 text-left tap-card animate-fade-in"
            >
              {/* Store image */}
              <img src={store.imageUrl} className="w-20 h-20 rounded-xl object-cover shrink-0" />

              {/* Content */}
              <div className="flex flex-col justify-center ml-3 min-w-0 flex-1">
                <h3 className="font-display font-semibold text-lg text-text-primary leading-tight truncate">
                  {store.name}
                </h3>
                <p className="text-xs font-body text-text-secondary truncate mt-0.5">
                  {store.nameEn}
                </p>

                <div className="flex items-center gap-1 mt-1">
                  {/* レビューが無い店は「★0」でなく「新着」表示 */}
                  {store.rating > 0 ? (
                    <>
                      <Star size={14} className="text-gold fill-gold" />
                      <span className="text-sm font-body text-text-primary font-medium">
                        {store.rating}
                      </span>
                      <span className="text-sm font-body text-text-secondary">
                        ({store.reviewCount})
                      </span>
                    </>
                  ) : (
                    <span className="text-sm font-body text-text-tertiary">
                      {t('stores.newStore')}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-sm">{categoryIcons[storeVisualKey(store)] || '🏪'}</span>
                  <span className="text-sm font-body text-text-secondary">
                    {t(storeTypeLabelKey(store))}
                    {distanceLabel && <> &bull; {distanceLabel}</>}
                  </span>
                </div>

                {/* #329 キッチンカー: 今日の出店状況 (スケジュール運用店のみ) */}
                {store.kitchenCarToday !== undefined && (
                  <div className="mt-1 flex items-center gap-1.5 min-w-0">
                    {store.kitchenCarToday ? (
                      <>
                        <span
                          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold font-body ${
                            store.kitchenCarToday.isOpenNow
                              ? 'bg-matcha/15 text-matcha'
                              : 'bg-gold/15 text-gold'
                          }`}
                        >
                          {store.kitchenCarToday.isOpenNow
                            ? t('stores.kcOpenNow')
                            : t('stores.kcTodayFrom', {
                                open: store.kitchenCarToday.openTime,
                                close: store.kitchenCarToday.closeTime,
                              })}
                        </span>
                        <span className="truncate font-body text-xs text-text-secondary">
                          📍 {store.kitchenCarToday.placeName}
                        </span>
                      </>
                    ) : (
                      <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium font-body text-text-tertiary">
                        {t('stores.kcClosedToday')}
                      </span>
                    )}
                  </div>
                )}

                {/* #8 実データ推薦の理由バッジ + #972 現金のみ運用店のバッジ。
                  * #2115 メニューを見せるだけの店 (menu-only) には出さない — アプリで払わない店なので、
                  * 「現金のみ」は支払い方の案内として誤り (詳細ページも同じ理由で出していない) */}
                {(recoScores[store.id]?.reason || showCashOnly(store)) && (
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <StoreRecoBadge reason={recoScores[store.id]?.reason} />
                    {showCashOnly(store) && (
                      <span className="inline-flex items-center rounded-full bg-matcha/15 text-matcha px-2 py-0.5 text-[11px] font-bold font-body">
                        {t('stores.cashOnly')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </button>
          )
        })}

        {displayed.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-4xl mb-3">🔍</p>
            <p className="text-text-secondary font-body text-sm">
              {t('stores.noStores')}
            </p>
          </div>
        )}
      </div>

      {/* Scroll to top */}
      <ScrollToTopButton />

      {/* Sort Modal */}
      <SortModal
        open={sortModalOpen}
        current={effectiveSort}
        distanceAvailable={distanceAvailable}
        recommendedAvailable={recoAvailable}
        onClose={() => setSortModalOpen(false)}
        onSelect={setSortMode}
      />

      {/* Tab Bar */}
      <TabBar />
    </div>
  )
}
